import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ImportedStudent, ImportResult, ImportStep } from '@/components/import/types';
import { useToast } from '@/hooks/use-toast';
// @ts-ignore
import * as XLSX from 'xlsx';

export const useStudentImport = (students: any[], refetchStudents: () => void) => {
  const [importedStudents, setImportedStudents] = useState<ImportedStudent[]>([]);
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);
  const [step, setStep] = useState<ImportStep>('idle');
  const [fileName, setFileName] = useState<string>('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = evt.target?.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json: ImportedStudent[] = XLSX.utils.sheet_to_json(sheet);
      setImportedStudents(json);
      setStep('preview');
    };
    reader.readAsBinaryString(file);
  };

  const handleActivityToggle = (id: string) => {
    setSelectedActivities((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const handleImport = async () => {
    if (isImporting) return;
    
    setIsImporting(true);
    toast({
      title: "Iniciando importación",
      description: `Importando ${importedStudents.length} estudiantes...`,
    });

    const result: ImportResult = {
      imported: [],
      duplicated: [],
      errors: [],
      total: importedStudents.length
    };

    for (const s of importedStudents) {
      // Buscar duplicado por email o nombre+clase
      const existing = students.find((st: any) =>
        (s.email && st.email && st.email.toLowerCase() === s.email.toLowerCase()) ||
        (st.name.trim().toLowerCase() === s.nombre.trim().toLowerCase() && st.class.trim().toLowerCase() === s.clase.trim().toLowerCase())
      );
      
      let studentId = existing?.id;
      
      if (existing) {
        // Verificar si ya está inscrito en todas las actividades seleccionadas
        const { data: existingActivities } = await supabase
          .from('student_activities')
          .select('activity_id')
          .eq('student_id', existing.id)
          .eq('status', 'active')
          .in('activity_id', selectedActivities);
        
        const alreadyEnrolledActivities = new Set((existingActivities || []).map(ea => ea.activity_id));
        const newActivities = selectedActivities.filter(actId => !alreadyEnrolledActivities.has(actId));
        
        if (newActivities.length === 0) {
          // Si ya está inscrito en todas las actividades seleccionadas
          result.duplicated.push({ ...s, motivo: 'Ya inscrito en todas las actividades seleccionadas' });
          continue;
        }
        
        studentId = existing.id;
      } else {
        // Alta alumno
        const { data, error } = await supabase.from('students').insert({
          name: s.nombre,
          class: s.clase,
          email: s.email,
          phone: s.telefono
        }).select();
        
        if (error || !data || !data[0]) {
          result.errors.push({ ...s, motivo: 'Error al crear alumno' });
          continue;
        }
        studentId = data[0].id;
        // NO push here, will push after successful enrollment
        // result.imported.push(s);
        // Refrescar después de crear un estudiante
        await refetchStudents();
      }
      
      // Inscribir a actividades seleccionadas
      let inscriptionError = false;
      for (const actId of selectedActivities) {
        // Comprobar si ya está inscrito
        const { data: insc, error: inscError } = await supabase
          .from('student_activities')
          .select('*')
          .eq('student_id', studentId)
          .eq('activity_id', actId)
          .eq('status', 'active');
          
        if (inscError) {
          inscriptionError = true;
          continue;
        }
        
        if (insc && insc.length > 0) {
          // Ya está inscrito y activo, no hacer nada
          continue;
        } else {
          // Buscar si existe una inscripción inactiva
          const { data: inactiveInsc, error: inactiveError } = await supabase
            .from('student_activities')
            .select('*')
            .eq('student_id', studentId)
            .eq('activity_id', actId)
            .eq('status', 'inactive');
            
          if (inactiveError) {
            inscriptionError = true;
            continue;
          }
          
          if (inactiveInsc && inactiveInsc.length > 0) {
            // Reactivar inscripción existente
            const { error: updateError } = await supabase
              .from('student_activities')
              .update({ status: 'active' })
              .eq('id', inactiveInsc[0].id);
            
            if (updateError) {
              inscriptionError = true;
            } else {
              // Refrescar después de reactivar una inscripción
              await refetchStudents();
            }
          } else {
            // Crear nueva inscripción
            const { error: insertError } = await supabase
              .from('student_activities')
              .insert({
                student_id: studentId,
                activity_id: actId,
                status: 'active'
              });
            
            if (insertError) {
              inscriptionError = true;
            } else {
              // Refrescar después de crear una inscripción
              await refetchStudents();
            }
          }
        }
      }
      
      if (inscriptionError) {
        if (!existing) {
          // Rollback: eliminar el alumno recién creado
          await supabase.from('students').delete().eq('id', studentId);
          await refetchStudents();
        }
        result.errors.push({ ...s, motivo: 'Error al inscribir en algunas actividades' + (!existing ? ', se eliminó el alumno creado' : '') });
      } else {
        result.imported.push(s);
      }
    }
    
    setImportResult(result);
    setIsImporting(false);
    
    // Toast de resultado
    toast({
      title: "Importación completada",
      description: `${result.imported.length} estudiantes importados, ${result.duplicated.length} duplicados, ${result.errors.length} errores`,
      variant: result.errors.length > 0 ? "destructive" : "default",
    });
    
    setStep('idle');
    setImportedStudents([]);
    setSelectedActivities([]);
    setFileName('');
    refetchStudents();
  };

  return {
    importedStudents,
    selectedActivities,
    step,
    fileName,
    importResult,
    isImporting,
    handleFileChange,
    handleActivityToggle,
    handleImport
  };
};